import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  useDeleteLifeInsuranceTariff,
  useFilterLifeInsuranceTariffs,
} from "../../../../common/api/queries/lifeInsuranceTariff";
import ActionTextIcon from "../../../../common/components/icons/ActionTextIcon";
import PopconfirmDeleteIcon from "../../../../common/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "../../../../common/components/views/Ellipsis";
import { TableSizes } from "../../../../common/constants";
import { LifeInsuranceTariff, LifeInsuranceTariffFilterPageRequest } from "../../types";
import PrimaryTariffTag from "../PrimaryTariffTag";

interface Props {
  searchParams: LifeInsuranceTariffFilterPageRequest;
  onUpdateClick: (tariff: LifeInsuranceTariff) => void;
}

export const LifeInsuranceTariffTable = ({ searchParams, onUpdateClick }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data, isFetching } = useFilterLifeInsuranceTariffs(searchParams);
  const deleteTariff = useDeleteLifeInsuranceTariff();

  const columns: ColumnsType<LifeInsuranceTariff> = [
    {
      key: "group",
      title: t("lifeInsuranceTariff.attrs.groupId"),
      width: 160,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.group.name}</Ellipsis>,
    },
    {
      key: "code",
      title: t("lifeInsuranceTariff.attrs.code"),
      width: 60,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.code}</Ellipsis>,
    },
    {
      key: "name",
      title: t("lifeInsuranceTariff.attrs.name"),
      width: 420,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.name}</Ellipsis>,
    },
    {
      key: "primary",
      align: "center",
      width: 80,
      render: (_, record) => record.primaryTariff && <PrimaryTariffTag style={{ marginRight: 0 }} />,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 180,
      render: (_, record) => (
        <div className="nowrap">
          <ActionTextIcon icon="edit" color="blue" text={t("common.edit")} onClick={() => onUpdateClick(record)} />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("lifeInsuranceTariff.helpers.tariffDeleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            cancelText={t("common.no")}
            okText={t("common.yes")}
            okType="danger"
            onConfirm={() => deleteTariff.mutate(record.id)}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <Table<LifeInsuranceTariff>
      {...tableStandardProps()}
      columns={columns}
      scroll={{ x: TableSizes.MEDIUM }}
      dataSource={data?.pageData}
      loading={isFetching}
      pagination={{
        ...paginationTableProps,
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
        onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
      }}
    />
  );
};
